import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import PageTitle from "../components/ui/PageTitle";
import api from "../../services/api";
import { STORAGE_URL } from "../../services/api";
import toast from "react-hot-toast";
import { User, Shield, Settings2, Plus, Clock, Activity, Save, Coins } from "lucide-react";

export default function Settings() {
  const [activeTab, setActiveTab] = useState("profile");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  // --- STATES ---
  const [profileData, setProfileData] = useState({
    name: "", email: "", phone: "", role: "", status: "", permissions: [], last_login: "", created_at: ""
  });

  const [passwords, setPasswords] = useState({
    new_password: "", confirm_password: ""
  });

  const [generalData, setGeneralData] = useState({
    // 🌟 NAYA: qrCode state added here
    companyName: "", logo: null, qrCode: null, contactEmail: "", contactNumber: "", officeAddress: "", copyrightText: "",
  });

  // 🌟 Coin System & Gateway States
  const [coinSettings, setCoinSettings] = useState({
    employer_coin_system: true,
    candidate_coin_system: true
  });
  const [paymentGateway, setPaymentGateway] = useState("razorpay"); // NAYA STATE

  // --- FETCH BOTH PROFILE & SETTINGS DATA ON LOAD ---
  useEffect(() => {
    const loadAllData = async () => {
      setFetching(true);
      try {
        const token = localStorage.getItem("admin_token");
        
        // 1. Fetch Profile
        const profileRes = await api.get("/user", {
          headers: { Authorization: `Bearer ${token}` }
        });
        const user = profileRes.data;
        setProfileData({
          name: user.name || "",
          email: user.email || "",
          phone: user.phone || "",
          role: user.role || "Admin",
          status: user.status || "Active",
          permissions: typeof user.permissions === 'string' ? JSON.parse(user.permissions) : (user.permissions || []),
          last_login: user.last_login || "Just now",
          created_at: user.created_at || ""
        });

        // 2. Fetch Website & Coin Settings
        const settingsRes = await api.get("/settings", {
            headers: { Authorization: `Bearer ${token}` }
        });
        if (settingsRes.data) {
          setGeneralData({
            companyName: settingsRes.data.company_name || "",
            logo: settingsRes.data.logo || null,
            // 🌟 NAYA: QR Code fetch from DB
            qrCode: settingsRes.data.qr_code_image || null,
            contactEmail: settingsRes.data.contact_email || "",
            contactNumber: settingsRes.data.contact_number || "",
            officeAddress: settingsRes.data.office_address || "",
            copyrightText: settingsRes.data.copyright_text || "",
          });
          
          // Set Coin Data
          setCoinSettings({
            employer_coin_system: settingsRes.data.employer_coin_system == 1,
            candidate_coin_system: settingsRes.data.candidate_coin_system == 1
          });

          // 🌟 Set Payment Gateway Data
          setPaymentGateway(settingsRes.data.payment_gateway || "razorpay");
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to load settings data");
      } finally {
        setFetching(false);
      }
    };

    loadAllData();
  }, []);

  // --- HANDLERS ---
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem("admin_token");
      await api.put("/profile/update", {
        name: profileData.name,
        email: profileData.email,
        phone: profileData.phone,
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success("Profile updated successfully!");
      
      const currentAdmin = JSON.parse(localStorage.getItem("admin"));
      localStorage.setItem("admin", JSON.stringify({ ...currentAdmin, name: profileData.name, email: profileData.email }));
    } catch (error) {
      toast.error(error.response?.data?.message || "Profile update failed");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (passwords.new_password !== passwords.confirm_password) {
      return toast.error("Passwords do not match!");
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem("admin_token");
      await api.put("/profile/update", {
        password: passwords.new_password
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success("Password changed successfully!");
      setPasswords({ new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error("Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  const handleGeneralSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem("admin_token");
      const formData = new FormData();
      formData.append("company_name", generalData.companyName);
      formData.append("contact_email", generalData.contactEmail);
      formData.append("contact_number", generalData.contactNumber);
      formData.append("office_address", generalData.officeAddress);
      formData.append("copyright_text", generalData.copyrightText);
      
      if (generalData.logo && typeof generalData.logo !== 'string') {
        formData.append("logo", generalData.logo);
      }
      
      // 🌟 NAYA: QR Code Image Append 
      if (generalData.qrCode && typeof generalData.qrCode !== 'string') {
        formData.append("qr_code_image", generalData.qrCode);
      }

      await api.post("/settings", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`
        },
      });

      toast.success("Website settings updated permanently!");
    } catch (error) {
      toast.error("Failed to update website settings.");
    } finally {
      setLoading(false);
    }
  };

  // Coin System Toggle Handler
  const handleCoinToggle = async (key) => {
    const newValue = !coinSettings[key];
    setCoinSettings({ ...coinSettings, [key]: newValue });

    try {
        const token = localStorage.getItem("admin_token");
        await api.post("/settings", {
            [key]: newValue ? '1' : '0'
        }, {
            headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Coin Setting updated successfully!`);
    } catch (error) {
        toast.error("Failed to update coin setting.");
        setCoinSettings({ ...coinSettings, [key]: !newValue }); // Revert on failure
    }
  };

  // 🌟 NAYA HANDLER: Payment Gateway Change
  const handleGatewayChange = async (e) => {
    const newValue = e.target.value;
    setPaymentGateway(newValue);

    try {
        const token = localStorage.getItem("admin_token");
        await api.post("/settings", {
            payment_gateway: newValue
        }, {
            headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Payment Gateway updated to ${newValue === 'razorpay' ? 'Razorpay' : 'Company QR'}!`);
    } catch (error) {
        toast.error("Failed to update payment gateway.");
    }
  };

  const getLogoPreview = () => {
    if (!generalData.logo) return null;
    if (typeof generalData.logo === 'string') {
      return STORAGE_URL(generalData.logo);
    }
    return URL.createObjectURL(generalData.logo);
  };

  const isSuperAdmin = profileData.role.toLowerCase() === 'super admin' || profileData.role.toLowerCase() === 'admin';

  return (
    <AdminLayout>
      <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <PageTitle title="Settings & Profile" subtitle="Manage your personal information and system preferences." />
        
        {isSuperAdmin && (
          <Link to="/employees/new" className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black px-5 py-2.5 rounded-lg font-bold transition-colors">
            <Plus size={18} /> Create Admin
          </Link>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        
        {/* ================= LEFT SIDEBAR TABS ================= */}
        <div className="w-full lg:w-64 flex-shrink-0 space-y-2 flex lg:flex-col overflow-x-auto pb-2 lg:pb-0 hide-scrollbar">
          <button onClick={() => setActiveTab("profile")} className={`whitespace-nowrap lg:w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors ${activeTab === "profile" ? "bg-yellow-400 text-black shadow-sm" : "text-gray-600 hover:bg-gray-100"}`}>
            <User size={18} /> My Profile
          </button>
          <button onClick={() => setActiveTab("security")} className={`whitespace-nowrap lg:w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors ${activeTab === "security" ? "bg-yellow-400 text-black shadow-sm" : "text-gray-600 hover:bg-gray-100"}`}>
            <Shield size={18} /> Security
          </button>
          <button onClick={() => setActiveTab("website")} className={`whitespace-nowrap lg:w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors ${activeTab === "website" ? "bg-yellow-400 text-black shadow-sm" : "text-gray-600 hover:bg-gray-100"}`}>
            <Settings2 size={18} /> Website Settings
          </button>
          
          {isSuperAdmin && (
            <button onClick={() => setActiveTab("coins")} className={`whitespace-nowrap lg:w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors ${activeTab === "coins" ? "bg-yellow-400 text-black shadow-sm" : "text-gray-600 hover:bg-gray-100"}`}>
              <Coins size={18} /> Coin System
            </button>
          )}
        </div>

        {/* ================= RIGHT CONTENT AREA ================= */}
        <div className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden min-h-[500px]">
          
          {fetching ? (
            <div className="flex justify-center items-center h-full pt-32 pb-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
            </div>
          ) : (
            <>
              {/* TAB 1: MY PROFILE */}
              {activeTab === "profile" && (
                <div className="p-6 md:p-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <h2 className="text-xl font-bold text-gray-800 mb-6">Profile Information</h2>
                  
                  {/* Info Card */}
                  <div className="flex flex-col md:flex-row gap-6 mb-8 bg-gray-50 p-6 rounded-xl border border-gray-100">
                    <div className="w-24 h-24 bg-yellow-400 text-gray-900 rounded-full flex items-center justify-center text-4xl font-bold shadow-sm shrink-0">
                      {profileData.name ? profileData.name.charAt(0).toUpperCase() : 'U'}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-xl font-bold text-gray-800">{profileData.name}</h3>
                        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${profileData.status.toLowerCase() === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {profileData.status}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-blue-600 mb-3">{profileData.role}</p>
                      
                      <div className="flex flex-col gap-2 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <Clock size={16} className="text-gray-400" />
                          <span>Last Login: <strong className="text-gray-800">{profileData.last_login}</strong></span>
                        </div>
                        <div className="flex items-start gap-2">
                          <Activity size={16} className="text-gray-400 mt-0.5" />
                          <div className="flex flex-wrap gap-1">
                            <span className="mr-1">Permissions:</span>
                            {profileData.permissions.length > 0 ? (
                              profileData.permissions.map((perm, i) => (
                                <span key={i} className="bg-gray-200 text-gray-700 px-2 py-0.5 rounded text-xs uppercase tracking-wider">{perm.replace('_', ' ')}</span>
                              ))
                            ) : (
                              <span className="font-semibold text-gray-800">All Access (Master Admin)</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Profile Edit Form */}
                  <form onSubmit={handleUpdateProfile} className="space-y-6 max-w-3xl">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                        <input type="text" required value={profileData.name} onChange={(e) => setProfileData({...profileData, name: e.target.value})} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-yellow-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                        <input type="email" required disabled value={profileData.email} onChange={(e) => setProfileData({...profileData, email: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 bg-gray-50 text-gray-500 rounded-lg outline-none cursor-not-allowed" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                        <input type="text" value={profileData.phone} onChange={(e) => setProfileData({...profileData, phone: e.target.value})} placeholder="+91 XXXXX XXXXX" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-yellow-500" />
                      </div>
                    </div>
                    <div className="pt-2">
                      <button type="submit" disabled={loading} className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-2.5 font-bold rounded-lg transition-colors disabled:opacity-70">
                        <Save size={18} /> {loading ? "Saving..." : "Save Changes"}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* TAB 2: SECURITY */}
              {activeTab === "security" && (
                <div className="p-6 md:p-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <h2 className="text-xl font-bold text-gray-800 mb-6">Change Password</h2>
                  <form onSubmit={handleUpdatePassword} className="space-y-6 max-w-md">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                      <input type="password" required value={passwords.new_password} onChange={(e) => setPasswords({...passwords, new_password: e.target.value})} placeholder="Min. 8 characters" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-yellow-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                      <input type="password" required value={passwords.confirm_password} onChange={(e) => setPasswords({...passwords, confirm_password: e.target.value})} placeholder="Repeat new password" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-yellow-500" />
                    </div>
                    <div className="pt-2">
                      <button type="submit" disabled={loading} className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-2.5 font-bold rounded-lg transition-colors disabled:opacity-70">
                        <Save size={18} /> {loading ? "Updating..." : "Update Password"}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* TAB 3: WEBSITE SETTINGS */}
              {activeTab === "website" && (
                <div className="p-6 md:p-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <h3 className="text-xl font-bold text-gray-800 mb-6">Frontend Website Settings</h3>
                  
                  <form onSubmit={handleGeneralSubmit} className="space-y-6 max-w-3xl">
                    <div className="pb-6 border-b border-gray-100">
                      <label className="block text-sm font-medium text-gray-700 mb-4">Website Logo</label>
                      <div className="flex items-center gap-6">
                        <div className="w-32 h-20 bg-gray-50 border border-gray-200 rounded-xl flex items-center justify-center overflow-hidden p-2">
                          {generalData.logo ? (
                            <img src={getLogoPreview()} alt="Logo" className="w-full h-full object-contain" />
                          ) : (
                            <span className="text-xs text-gray-400 text-center">No Logo</span>
                          )}
                        </div>
                        <div>
                          <input 
                            type="file" accept="image/*"
                            onChange={(e) => setGeneralData({...generalData, logo: e.target.files[0]})}
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-yellow-50 file:text-yellow-700 hover:file:bg-yellow-100 cursor-pointer"
                          />
                          <p className="text-xs text-gray-400 mt-2">Recommended size: 250x100px. PNG or JPG.</p>
                        </div>
                      </div>
                    </div>

                    {/* 🌟 NAYA: QR CODE UPLOAD BOX (Admin UI me added) */}
                    <div className="pb-6 border-b border-gray-100 mt-6">
                      <label className="block text-sm font-medium text-gray-700 mb-4">Payment QR Code (For Manual Recharges)</label>
                      <div className="flex items-center gap-6">
                        <div className="w-32 h-32 bg-gray-50 border border-gray-200 rounded-xl flex items-center justify-center overflow-hidden p-2">
                          {generalData.qrCode ? (
                            <img src={typeof generalData.qrCode === 'string' ? STORAGE_URL(generalData.qrCode) : URL.createObjectURL(generalData.qrCode)} alt="QR Code" className="w-full h-full object-contain" />
                          ) : (
                            <span className="text-xs text-gray-400 text-center">No QR Code</span>
                          )}
                        </div>
                        <div>
                          <input 
                            type="file" accept="image/*"
                            onChange={(e) => setGeneralData({...generalData, qrCode: e.target.files[0]})}
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                          />
                          <p className="text-xs text-gray-400 mt-2">Recommended: Clear UPI QR Code.</p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
                        <input type="text" value={generalData.companyName} onChange={(e) => setGeneralData({...generalData, companyName: e.target.value})} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-yellow-500" required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Copyright Text</label>
                        <input type="text" value={generalData.copyrightText} onChange={(e) => setGeneralData({...generalData, copyrightText: e.target.value})} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-yellow-500" required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Contact Email</label>
                        <input type="email" value={generalData.contactEmail} onChange={(e) => setGeneralData({...generalData, contactEmail: e.target.value})} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-yellow-500" required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number</label>
                        <input type="text" value={generalData.contactNumber} onChange={(e) => setGeneralData({...generalData, contactNumber: e.target.value})} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-yellow-500" required />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Office Address</label>
                      <textarea rows={3} value={generalData.officeAddress} onChange={(e) => setGeneralData({...generalData, officeAddress: e.target.value})} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-yellow-500 resize-none" required />
                    </div>

                    <div className="pt-2">
                      <button disabled={loading} type="submit" className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-2.5 px-6 rounded-lg transition-colors disabled:opacity-50">
                        <Save size={18} /> {loading ? "Saving..." : "Save Website Settings"}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* TAB 4: COIN SYSTEM SETTINGS */}
              {activeTab === "coins" && (
                <div className="p-6 md:p-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                    <Coins className="text-yellow-500" /> Coin System & Payment Controls
                  </h3>
                  <div className="space-y-6 max-w-3xl">
                    
                    {/* Employer Coin Toggle */}
                    <div className="flex items-center justify-between p-6 bg-gray-50 rounded-2xl border border-gray-200 shadow-sm">
                        <div>
                            <h4 className="text-[16px] font-bold text-gray-900">Employer Coin System (Pay Per Job Post)</h4>
                            <p className="text-sm font-medium text-gray-500 mt-1">If OFF, employers can post jobs for free without coin deduction.</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer ml-4 shrink-0">
                            <input type="checkbox" className="sr-only peer" checked={coinSettings.employer_coin_system} onChange={() => handleCoinToggle('employer_coin_system')} />
                            <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-yellow-500"></div>
                        </label>
                    </div>

                    {/* Candidate Coin Toggle */}
                    <div className="flex items-center justify-between p-6 bg-gray-50 rounded-2xl border border-gray-200 shadow-sm">
                        <div>
                            <h4 className="text-[16px] font-bold text-gray-900">Candidate Coin System (Pay Per Apply)</h4>
                            <p className="text-sm font-medium text-gray-500 mt-1">If OFF, candidates can apply to jobs for free without coin deduction.</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer ml-4 shrink-0">
                            <input type="checkbox" className="sr-only peer" checked={coinSettings.candidate_coin_system} onChange={() => handleCoinToggle('candidate_coin_system')} />
                            <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-yellow-500"></div>
                        </label>
                    </div>

                    {/* PAYMENT GATEWAY SELECTION */}
                    <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm mt-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Active Payment Gateway</h3>
                      <p className="text-sm text-gray-500 mb-4">Choose how users will pay for wallet recharges.</p>
                      
                      <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                        <label className={`flex-1 flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${paymentGateway === 'razorpay' ? 'border-yellow-500 bg-yellow-50' : 'border-gray-200 bg-gray-50 hover:bg-gray-100'}`}>
                          <input 
                            type="radio" 
                            name="gateway" 
                            value="razorpay" 
                            checked={paymentGateway === "razorpay"} 
                            onChange={handleGatewayChange}
                            className="w-4 h-4 text-yellow-600 focus:ring-yellow-500"
                          />
                          <div>
                            <p className="font-bold text-gray-900">Razorpay (Auto)</p>
                            <p className="text-xs text-gray-500">2% Comm., Instant Approval</p>
                          </div>
                        </label>

                        <label className={`flex-1 flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${paymentGateway === 'qr' ? 'border-yellow-500 bg-yellow-50' : 'border-gray-200 bg-gray-50 hover:bg-gray-100'}`}>
                          <input 
                            type="radio" 
                            name="gateway" 
                            value="qr" 
                            checked={paymentGateway === "qr"} 
                            onChange={handleGatewayChange}
                            className="w-4 h-4 text-yellow-600 focus:ring-yellow-500"
                          />
                          <div>
                            <p className="font-bold text-gray-900">Company QR (Manual)</p>
                            <p className="text-xs text-gray-500">0% Comm., UTR Verification</p>
                          </div>
                        </label>
                      </div>
                    </div>

                  </div>
                </div>
              )}

            </>
          )}

        </div>
      </div>
    </AdminLayout>
  );
}